import {
  type ChangeAcknowledgement,
  acknowledgementOf,
  type ChangeConsequences,
  type DgCoverageEntry,
  type DgQuestion,
  type DgQuestionType,
  type DraftArtifactKind,
  type ReconciliationItem,
  STALE_UPSTREAM_KINDS,
  DG_COVERAGE_POLL_MS,
  DG_COVERAGE_WAIT_MS,
  DG_DRAFT_POLL_MS,
  DG_DRAFT_WAIT_MS,
  MAX_DG_OPTION_CHARS,
  MAX_DG_QUESTION_CHARS,
  canonicalJson,
  dgCoverageCurrent,
  dgCoverageRequestNeeded,
  dgQuestionNumbers,
  dgDraftNeeded,
  dgDraftRequestNeeded,
  dgReconcileNeeded,
  dgRedraftOffered,
  reconciledVersionText,
  sameDgCoverageInputs,
  sameDgDraftInputs,
} from '@sigora/domain';
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  type GuideView,
  approveArtifact,
  fetchGuide,
  materialsFileUrl,
  requestGuideCoverage,
  requestGuideDraft,
  rewordGuideQuestion,
} from '../api/client.ts';
import { domainCodeOf } from '../api/errors.ts';
import { STATUS_LABELS } from '../artifacts/state.ts';
import { useOpenAskSigora } from './ask-sigora.ts';
import { ChangeConfirm, unacknowledgedChangeOf } from './ChangeConsequences.tsx';
import {
  COVERAGE_FIX_LABEL,
  COVERAGE_REDRAFT_OFFER,
  COVERAGE_REDRAFT_UNCHANGED,
  coverageFixMessage,
} from './guide-copy.ts';
import { guideMinutesLabel, plural } from './guide-minutes.ts';
import { type GuideEdit, applyReword } from './guide-reword.ts';
import {
  BEFORE_YOUR_CHANGE,
  CHANGE_MARK_LABELS,
  RECONCILED_REMOVED_HEADING,
  reconcileFailedText,
  reconcileOverdueText,
  reconcilePendingText,
  reconciledText,
  reconciledUnchangedText,
} from './reconcile-copy.ts';
import { useRequestWait } from './use-request-wait.ts';
import { Pending } from '../ui/Pending.tsx';
import {
  APPROVING_LABEL,
  EDITS_OPEN_HINT,
  GUIDE_APPROVE_ANYWAY_LABEL,
  GUIDE_CHECKING_HINT,
  GUIDE_CHECK_FAILED_HINT,
  GUIDE_DRAFT_FAILED_HINT,
  GUIDE_DRAFTING_HINT,
  GUIDE_GAP_HINT,
  GUIDE_REDRAFTING_HINT,
  GUIDE_RETRY_HINT,
  GUIDE_UPDATING_HINT,
  RQS_NOT_APPROVED,
  SAVING_HINT,
} from './continue-copy.ts';
import { type FirstDraft, firstDraftOf } from './first-draft.ts';
import { useRefusalFocus } from './refusal-focus.ts';
import { useContinueAction } from './step-footer.tsx';
import { VersionHistory, historyEntriesOf } from './VersionHistory.tsx';

export interface GuideStepProps {
  studyId: string;
  /**
   * Whether the study's inputs are fixed: paid for, frozen or launched. The step is then shown as
   * it stands, asking for nothing and offering no edit or approval the server would refuse.
   */
  frozen: boolean;
  /** Bumped by the shell whenever the study may have changed; the step is read again on it. */
  refreshToken: number;
  /** Tells the shell the guide changed, so the steps and the sheet are read again. */
  onStudyChanged: () => void;
  /** Hands the shell the turn an approval started, which drafts what comes next. */
  onApproved: (approved: { kind: DraftArtifactKind; turnId: string | null }) => void;
}

/** What the step says when what it showed moved under an edit or an approval. */
const CHANGED_NOTICE = 'This guide changed; look again.';

/**
 * The refusals that mean the guide on screen is no longer the one the server holds: a version that
 * moved, or a change above it that landed after the render. A reword and an approval both read the
 * step again on either.
 */
const MOVED_CODES = new Set(['artifact_version_not_current', 'artifact_stale']);

/**
 * What an approval refused over the coverage check says. Placeholder copy. The step is read again
 * beside it, so the check's own lines show what it found.
 */
const COVERAGE_REFUSALS: Record<string, string> = {
  dg_coverage_not_checked: 'The guide has to be checked against your research questions first.',
  dg_coverage_gap: 'The check found a research question this guide does not answer.',
  dg_coverage_uncertain: `Sigora is not sure this guide answers every research question. Press "${GUIDE_APPROVE_ANYWAY_LABEL}" to go ahead.`,
  rqs_not_approved: RQS_NOT_APPROVED,
};

/** How each kind of question is named on the card. Placeholder copy. */
const KIND_LABELS: Record<DgQuestionType, string> = {
  open_response: 'Open question',
  single_choice: 'Poll — one answer',
  multiple_choice: 'Poll — several answers',
};

/** What the card says under an image a question shows. Placeholder copy. */
const IMAGE_CAPTION = 'Shown before this question';

/** What the card says while an image marked to show is shown on no question. Placeholder copy. */
const UNSHOWN_IMAGES_HINT =
  'You marked an image to show participants that this guide does not show yet. Ask Sigora below to show it with a question.';

/**
 * Whether the guide is one a request may be made for: none yet, one waiting on a change above it,
 * which is reconciled, or one whose current coverage check found a gap, redrafted on request.
 */
const askable = (dg: Parameters<typeof dgDraftNeeded>[0]): boolean =>
  dgDraftNeeded(dg) || dgReconcileNeeded(dg) || dgRedraftOffered(dg);

const failureMessage = (failure: unknown, fallback: string): string =>
  failure instanceof Error ? failure.message : fallback;

/** "3 questions · 1 poll": what a section asks, counted from its questions. */
const sectionCounts = (questions: readonly DgQuestion[]): string => {
  const polls = questions.filter((question) => question.type !== 'open_response').length;
  const open = questions.length - polls;

  return [
    open === 0 ? null : plural(open, 'question', 'questions'),
    polls === 0 ? null : plural(polls, 'poll', 'polls'),
  ]
    .filter((part) => part !== null)
    .join(' · ');
};

/**
 * What the researcher can do about the lines in a coverage check's notice: ask Sigora in the
 * step's conversation to fix them, or have Sigora redraft the guide for them, which changes what
 * fails them and keeps the rest. Placeholder copy.
 */
const CoverageActions = ({
  onFix,
  fixDisabled,
  onRedraft,
  disabled,
  redraftChangedNothing,
}: {
  /** Opens the step's conversation and sends Sigora the lines to fix, or `null` with none to name. */
  onFix: (() => void) | null;
  /** Whether "Help me fix this" is held: see `fixHeld`. */
  fixDisabled: boolean;
  /** Asks for the guide to be redrafted, or `null` while that is not on offer. */
  onRedraft: (() => void) | null;
  disabled: boolean;
  /** The last redraft over these lines found nothing to change, so the guide on screen is the same. */
  redraftChangedNothing: boolean;
}) => (
  <>
    {onFix !== null && (
      <div className="guide-coverage-gap-actions">
        <button
          type="button"
          data-test-id="guide-coverage-fix"
          disabled={fixDisabled}
          onClick={onFix}
        >
          {COVERAGE_FIX_LABEL}
        </button>
      </div>
    )}
    {redraftChangedNothing && (
      <p data-test-id="guide-coverage-redraft-unchanged">{COVERAGE_REDRAFT_UNCHANGED}</p>
    )}
    {onRedraft !== null && (
      <div className="guide-coverage-gap-actions">
        <p>{COVERAGE_REDRAFT_OFFER}</p>
        <button
          type="button"
          data-test-id="guide-coverage-gap-redraft"
          disabled={disabled}
          onClick={onRedraft}
        >
          Redraft the guide
        </button>
      </div>
    )}
  </>
);

/**
 * The research questions the coverage check found nothing in the guide answering, each with the
 * judge's reason, and what the researcher can do about them. Continue is held while any is shown.
 * Placeholder copy.
 */
const CoverageGaps = ({
  gaps,
  rqText,
  actions,
}: {
  gaps: readonly DgCoverageEntry[];
  rqText: ReadonlyMap<string, string>;
  /** What can be done about them; the step's one set, shown here whenever a gap is. */
  actions: ReactNode;
}) => (
  <div className="guide-notice" data-test-id="guide-coverage-gaps">
    <p className="guide-coverage-title">Before you approve</p>
    <ul>
      {gaps.map((entry) => (
        <li key={entry.rq_id} data-test-id="guide-coverage-gap" data-rq-id={entry.rq_id}>
          <p>Nothing here answers: {rqText.get(entry.rq_id) ?? 'a research question'}</p>
          {entry.reason !== '' && <p className="guide-coverage-reason">{entry.reason}</p>}
        </li>
      ))}
    </ul>
    {actions}
  </div>
);

/**
 * The research questions the coverage check is not sure the guide answers, each with the judge's
 * reason. They hold nothing: Continue approves anyway. When no gap shows, this notice carries what
 * can be done about them. Placeholder copy.
 */
const CoverageUncertain = ({
  uncertain,
  rqText,
  actions,
}: {
  uncertain: readonly DgCoverageEntry[];
  rqText: ReadonlyMap<string, string>;
  /** What can be done about them, or `null` while the gap notice above carries it. */
  actions: ReactNode;
}) => (
  <div className="guide-notice" data-test-id="guide-coverage-uncertain">
    <ul>
      {uncertain.map((entry) => (
        <li key={entry.rq_id} data-test-id="guide-coverage-uncertain-item" data-rq-id={entry.rq_id}>
          <p>
            Sigora is not sure this guide answers:{' '}
            {rqText.get(entry.rq_id) ?? 'a research question'}
          </p>
          {entry.reason !== '' && <p className="guide-coverage-reason">{entry.reason}</p>}
        </li>
      ))}
    </ul>
    {actions}
  </div>
);

/** An edit can be saved once the question and every one of its options say something. */
const savable = (edit: GuideEdit) =>
  edit.text.trim() !== '' && edit.options.every((option) => option.text.trim() !== '');

/**
 * The Guide step: the discussion guide as participants will see it, section by section, with the
 * minutes it asks of each of them. The footer's Continue approves it.
 *
 * Everything shown is the server's: the guide, the requests behind it, the coverage check and every
 * version with what produced it. So a refresh restores the step by reading it again, and nothing
 * here decides what the server will accept — a reword and an approval both name the version on
 * screen, and the server refuses either over a guide that moved.
 *
 * The researcher changes the wording of one question at a time — its text and its options' text,
 * never their number or order — and may have several edits open; approval waits until none is.
 * Adding, removing, moving or recasting a question is asked for in the step's conversation, whose
 * turns end with the step read again.
 *
 * The guide is asked for on arrival, once per set of inputs, when the study holds none, and
 * reconciled on arrival when it is waiting on a change above it: the step shows the earlier version
 * as it was before the change while the reconciliation is pending, says so when it fails, and once
 * it lands marks each question it changed or added with the reason it gave, until the guide is
 * approved. An unapproved guide is checked against its research questions on arrival and after
 * every change the step reads back, so a research question it does not answer is shown before
 * the footer's Continue approves it rather than after.
 */
export const GuideStep = ({
  studyId,
  frozen,
  refreshToken,
  onStudyChanged,
  onApproved,
}: GuideStepProps) => {
  const openAskSigora = useOpenAskSigora();
  /**
   * The coverage check "Help me fix this" was last sent over. The step does not see the turn it
   * starts, so the button sends once per check; a new check, as Sigora's edit brings, offers it
   * again.
   */
  const [fixSentFor, setFixSentFor] = useState<string | null>(null);
  const [view, setView] = useState<GuideView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Why the step was read again under the reader, said once over it. */
  const [notice, setNotice] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [checking, setChecking] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  /** The sections the reader opened; every section starts closed. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  /** Every open edit, by question id. */
  const [edits, setEdits] = useState<ReadonlyMap<string, GuideEdit>>(new Map());
  /** The question whose reword is out. */
  const [saving, setSaving] = useState<string | null>(null);
  /**
   * A reword or an approval has landed and the read that follows it has not. The step then holds
   * every save and the approval: the guide on screen is the one the write replaced, so a save or an
   * approval made over it would quote a version that moved, or approve the same version twice.
   */
  const [settling, setSettling] = useState(false);
  /**
   * The first read that can settle a write: the one it started, or any newer one. An older read
   * that lands after the write, superseded, says nothing about what it wrote.
   */
  const settlesFrom = useRef<number | null>(null);
  /** What the server refused a reword with, on the question it was made on. */
  const [editErrors, setEditErrors] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * The reword waiting on the reader's say-so, with what the server said it would do below the
   * guide. What they typed stays open under it either way.
   */
  const [confirming, setConfirming] = useState<{
    questionId: string;
    consequences: ChangeConsequences;
  } | null>(null);

  /**
   * Only the newest answer is shown. A poll's read and a request's answer can cross, and the older
   * landing last would put back a guide or a request the reader has just moved past.
   */
  const latest = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  const editButtons = useRef(new Map<string, HTMLButtonElement>());
  const sectionToggles = useRef(new Map<string, HTMLButtonElement>());
  /**
   * The guide version and the check the step last showed. An approval refused over one of them
   * says nothing about the next, so a read that brings a different one drops the refusal.
   */
  const shownPair = useRef<string | null>(null);
  const editFields = useRef(new Map<string, HTMLTextAreaElement>());
  /** The question an edit was just opened on, whose field takes the focus. */
  const [focusField, setFocusField] = useState<string | null>(null);
  /** The question an edit was just closed on, whose Edit button takes the focus back. */
  const [returnFocusTo, setReturnFocusTo] = useState<string | null>(null);

  /** Something is in flight that a reword or an approval must not race. */
  const busy = asking || checking || approving || saving !== null || settling;

  const issue = () => {
    latest.current += 1;

    return latest.current;
  };

  const load = useCallback(
    async (signal?: AbortSignal) => {
      latest.current += 1;
      const ticket = latest.current;
      /** The newest read has landed, one way or the other, so a write waiting on it is settled. */
      const settle = () => {
        if (settlesFrom.current !== null && ticket >= settlesFrom.current) {
          settlesFrom.current = null;
          setSettling(false);
        }
      };

      try {
        const next = await fetchGuide(studyId, signal);
        if (alive.current && ticket === latest.current) {
          setView(next);
          setLoadError(null);
          const pair = `${String(next.dg?.version ?? null)}|${next.coverage?.id ?? ''}|${next.coverage?.status ?? ''}`;
          if (shownPair.current !== null && shownPair.current !== pair) {
            setApproveError(null);
          }
          shownPair.current = pair;
          // A request that failed on the way stops mattering once nothing is offered to draft.
          if (!askable(next.dg)) {
            setRequestError(null);
          }
          settle();
        }
      } catch (failure: unknown) {
        // A read given up on is not a read that failed: the deadline that aborted it says so itself.
        if (alive.current && ticket === latest.current && signal?.aborted !== true) {
          setLoadError(failureMessage(failure, 'Your guide could not be read.'));
          settle();
        }
      }
    },
    [studyId],
  );

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  /** Holds the step after a write until a read started now, or a newer one, has landed. */
  const holdUntilRead = () => {
    setSettling(true);
    // The read this starts is the next ticket; any newer one settles the write as well.
    settlesFrom.current = latest.current + 1;
    void load();
  };

  /**
   * Asks for a draft, shows the request the server stored, and then reads the whole step again,
   * which is what makes the answer stick: a read started while the request was in flight can have
   * looked before it committed and still land last.
   */
  const askDraft = useCallback(
    (again: boolean) => {
      setAsking(true);
      setRequestError(null);
      const ticket = issue();

      void requestGuideDraft(studyId, { again }).then(
        (draft) => {
          setAsking(false);
          if (alive.current && ticket === latest.current && draft !== null) {
            setView((current) => (current === null ? current : { ...current, draft }));
          }
          void load();
        },
        (failure: unknown) => {
          setAsking(false);
          if (alive.current) {
            setRequestError(failureMessage(failure, 'Sigora could not be asked for a guide.'));
          }
          void load();
        },
      );
    },
    [studyId, load],
  );

  /** Asks for the guide to be checked, on the same terms as `askDraft`. */
  const askCoverage = useCallback(
    (again: boolean) => {
      setChecking(true);
      setCoverageError(null);
      const ticket = issue();

      void requestGuideCoverage(studyId, { again }).then(
        (coverage) => {
          setChecking(false);
          if (alive.current && ticket === latest.current && coverage !== null) {
            setView((current) => (current === null ? current : { ...current, coverage }));
          }
          void load();
        },
        (failure: unknown) => {
          setChecking(false);
          if (alive.current) {
            setCoverageError(
              failureMessage(failure, 'Sigora could not be asked to check the guide.'),
            );
          }
          void load();
        },
      );
    },
    [studyId, load],
  );

  const dg = view?.dg ?? null;
  const rqs = view?.rqs ?? null;
  const authorizes = rqs?.authorizes === true;
  const currentInputs = view?.currentInputs ?? null;
  const coverageInputs = view?.coverageInputs ?? null;
  /** No guide yet: it is only ever drafted, not edited. */
  const draftNeeded = view !== null && dgDraftNeeded(dg);
  /** A guide waiting on a change above it, which is reconciled rather than edited. */
  const reconciling = view !== null && dgReconcileNeeded(dg);
  /** What the guide is waiting on, in the order a change travels. */
  const staleKinds = STALE_UPSTREAM_KINDS.filter(
    (kind) => dg?.staleness.some((window) => window.upstream_kind === kind) ?? false,
  );
  const stale = dg !== null && (dg.status === 'stale' || dg.staleness.length > 0);

  const draft = view?.draft ?? null;
  const draftForNow =
    draft !== null && currentInputs !== null && sameDgDraftInputs(draft.inputs, currentInputs)
      ? draft
      : null;
  const draftPendingId = draftForNow?.status === 'pending' ? draftForNow.id : null;
  const draftOverdue = useRequestWait({
    pendingId: draftPendingId,
    pollMs: DG_DRAFT_POLL_MS,
    waitMs: DG_DRAFT_WAIT_MS,
    load,
    alive,
  });
  const draftWaiting = draftPendingId !== null && !draftOverdue;

  /** The guide as it stands can be checked: there, unapproved, waiting on nothing, authorized. */
  const checkable = !frozen && dg !== null && dg.status === 'unapproved' && !stale && authorizes;
  const coverage = view?.coverage ?? null;
  const coverageForNow =
    checkable &&
    coverage !== null &&
    coverageInputs !== null &&
    sameDgCoverageInputs(coverage.inputs, coverageInputs)
      ? coverage
      : null;
  const coveragePendingId = coverageForNow?.status === 'pending' ? coverageForNow.id : null;
  const coverageOverdue = useRequestWait({
    pendingId: coveragePendingId,
    pollMs: DG_COVERAGE_POLL_MS,
    waitMs: DG_COVERAGE_WAIT_MS,
    load,
    alive,
  });
  const coverageWaiting = coveragePendingId !== null && !coverageOverdue;

  /**
   * Arrival: ask once for the study's inputs when a draft is needed and no request for those inputs
   * is still worth waiting on. Keyed on the inputs, so research questions approved again while the
   * step is open ask again, and a request that failed is not repeated on every read; "Try again" is
   * how it is asked for again.
   */
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (view === null || currentInputs === null || frozen) {
      return;
    }

    const key = canonicalJson(currentInputs);
    if (askedFor.current === key) {
      return;
    }

    askedFor.current = key;
    if (
      dgDraftRequestNeeded({
        request: view.draft,
        inputs: currentInputs,
        dg: view.dg,
        now: new Date(),
      })
    ) {
      askDraft(false);
    }
  }, [view, currentInputs, frozen, askDraft]);

  /**
   * The coverage check, asked once per guide and research-question version while the guide can be
   * checked, and not while a write is settling: the guide on screen then is the one it replaced.
   */
  const checkedFor = useRef<string | null>(null);
  useEffect(() => {
    if (view === null || coverageInputs === null || !checkable || settling) {
      return;
    }

    const key = canonicalJson(coverageInputs);
    if (checkedFor.current === key) {
      return;
    }

    checkedFor.current = key;
    if (
      dgCoverageRequestNeeded({ request: view.coverage, inputs: coverageInputs, now: new Date() })
    ) {
      askCoverage(false);
    }
  }, [view, coverageInputs, checkable, settling, askCoverage]);

  useEffect(() => {
    if (focusField !== null) {
      editFields.current.get(focusField)?.focus();
      setFocusField(null);
    }
  }, [focusField]);

  /**
   * The focus goes back to the closed edit's "Edit" button, or — when the reader closed its
   * section while the edit was open, so the button is not drawn — to that section's toggle.
   */
  useEffect(() => {
    if (returnFocusTo !== null && !edits.has(returnFocusTo)) {
      const section = view?.dg?.content.sections.find((held) =>
        held.questions.some((question) => question.id === returnFocusTo),
      );
      (
        editButtons.current.get(returnFocusTo) ??
        (section === undefined ? undefined : sectionToggles.current.get(section.id))
      )?.focus();
      setReturnFocusTo(null);
    }
  }, [returnFocusTo, edits, view]);

  const setEditError = (questionId: string, message: string | null) => {
    setEditErrors((errors) => {
      const next = new Map(errors);
      if (message === null) {
        next.delete(questionId);
      } else {
        next.set(questionId, message);
      }

      return next;
    });
  };

  const closeEdit = (questionId: string) => {
    setEdits((open) => {
      const next = new Map(open);
      next.delete(questionId);

      return next;
    });
    setEditError(questionId, null);
    setConfirming((current) => (current?.questionId === questionId ? null : current));
    setReturnFocusTo(questionId);
  };

  const openEdit = (question: DgQuestion) => {
    setEdits((open) =>
      new Map(open).set(question.id, {
        text: question.text,
        options: (question.options ?? []).map((option) => ({ id: option.id, text: option.text })),
      }),
    );
    setEditError(question.id, null);
    setFocusField(question.id);
  };

  const typeInto = (questionId: string, change: (edit: GuideEdit) => GuideEdit) => {
    // The confirm was for the wording as it was sent.
    setConfirming((current) => (current?.questionId === questionId ? null : current));
    setEdits((open) => {
      const edit = open.get(questionId);

      return edit === undefined ? open : new Map(open).set(questionId, change(edit));
    });
  };

  /** A refusal that says the guide on screen moved: say so once, and read it again. */
  const moved = () => {
    setNotice(CHANGED_NOTICE);
    void load();
    onStudyChanged();
  };

  const save = (
    question: DgQuestion,
    acknowledgedDownstream: ChangeAcknowledgement | null = null,
  ) => {
    const edit = edits.get(question.id);
    if (dg === null || edit === undefined || busy || !savable(edit)) {
      return;
    }

    const options = question.options === undefined ? undefined : edit.options;
    setSaving(question.id);
    setEditError(question.id, null);
    setConfirming(null);
    setNotice(null);

    void rewordGuideQuestion({
      studyId,
      questionId: question.id,
      text: edit.text,
      ...(options === undefined ? {} : { options }),
      expectedVersion: dg.version,
      acknowledgedDownstream,
    }).then(
      ({ version, deduped }) => {
        setSaving(null);
        setApproveError(null);
        closeEdit(question.id);
        // The reword on screen at once, in the researcher's words and at the version it wrote, so
        // nothing flickers back to the old wording while the read that confirms it is out.
        if (!deduped) {
          setView((current) =>
            current === null ? current : applyReword(current, question.id, edit, version),
          );
        }
        holdUntilRead();
        onStudyChanged();
      },
      (failure: unknown) => {
        setSaving(null);
        const consequences = unacknowledgedChangeOf(failure);
        if (consequences !== null) {
          setConfirming({ questionId: question.id, consequences });
          return;
        }

        const code = domainCodeOf(failure);

        if (code !== null && MOVED_CODES.has(code)) {
          // What was typed stays open over the guide as it is now, and saving it again names it.
          moved();
          return;
        }

        if (code === 'dg_question_not_found') {
          closeEdit(question.id);
          moved();
          return;
        }

        setEditError(question.id, failureMessage(failure, 'That wording could not be saved.'));
      },
    );
  };

  const rqText = new Map((rqs?.items ?? []).map((item) => [item.id, item.text]));
  const verdict =
    coverageForNow !== null && dgCoverageCurrent(coverageForNow, coverageInputs)
      ? coverageForNow.verdict
      : null;
  const gaps = verdict?.rqs.filter((entry) => entry.verdict === 'gap') ?? [];
  const uncertain = verdict?.rqs.filter((entry) => entry.verdict === 'uncertain') ?? [];
  /** The check answers for the guide on screen and found nothing that blocks it. */
  const coverageClear = verdict !== null && gaps.length === 0;
  const anyway = coverageClear && uncertain.length > 0;
  /** The check found a research question the guide does not answer, or may not: what a redraft is offered over. */
  const coverageUnanswered = gaps.length > 0 || uncertain.length > 0;

  /**
   * Approves the guide on screen, from Continue, anyway over uncertain coverage as Continue's label
   * then says; true once accepted.
   */
  const approve = (): Promise<boolean> => {
    if (dg === null || busy) {
      return Promise.resolve(false);
    }

    setApproving(true);
    setApproveError(null);
    setNotice(null);

    return approveArtifact({
      studyId,
      artifactId: dg.artifactId,
      version: dg.version,
      acceptUncertainCoverage: anyway,
    }).then(
      ({ turnId }) => {
        setApproving(false);
        onApproved({ kind: 'dg', turnId });
        holdUntilRead();
        onStudyChanged();
        return true;
      },
      (failure: unknown) => {
        setApproving(false);
        const code = domainCodeOf(failure);

        if (code !== null && MOVED_CODES.has(code)) {
          moved();
          return false;
        }

        setApproveError(
          (code === null ? undefined : COVERAGE_REFUSALS[code]) ??
            failureMessage(failure, 'This guide could not be approved.'),
        );
        if (code !== null) {
          void load();
        }
        return false;
      },
    );
  };

  /** The guide as it stands can be reworded: there, waiting on nothing above it. */
  const editable = !frozen && dg !== null && !stale && !draftNeeded;
  /**
   * The edits still open over the guide on screen. An edit of a question a read no longer carries —
   * removed in the step's conversation, or redrafted — is gone with it, and holds up nothing.
   */
  const shownIds = new Set(
    dg?.content.sections.flatMap((section) => section.questions.map((question) => question.id)) ??
      [],
  );
  const openEdits = editable ? [...edits.keys()].filter((id) => shownIds.has(id)).length : 0;
  const coverageFailed =
    coverageForNow?.status === 'unavailable' || coverageForNow?.status === 'unreadable';
  const coverageLate = coveragePendingId !== null && coverageOverdue;
  const draftFailed = draftForNow?.status === 'unavailable' || draftForNow?.status === 'unreadable';
  /**
   * A repair that found nothing to change writes no version, so its `ready` request still names the
   * guide on screen; any other answered request moved the guide and no longer matches its inputs.
   */
  const redraftChangedNothing = !reconciling && dg !== null && draftForNow?.status === 'ready';
  const draftLate = draftPendingId !== null && draftOverdue;
  /**
   * A request refused or lost on the way stores nothing, so there is no failed request to show; the
   * refusal on screen is the only trace of it, and the retry is offered beside it.
   */
  const retryOffered =
    !frozen &&
    currentInputs !== null &&
    askable(dg === null ? null : { ...dg, coverageUnanswered }) &&
    (draftFailed || draftLate || requestError !== null);
  /** The research questions do not authorize a guide, so none can be drafted or approved. */
  const needsRqs = view !== null && !frozen && !authorizes && dg?.status !== 'approved';
  /**
   * No guide yet, and nothing late, failed or refused to say instead: the first draft is being asked
   * for or is under way, so the step says so from the moment it asks, not only once the request is
   * stored.
   */
  const firstDraftUnderWay =
    dg === null && !frozen && !needsRqs && !draftLate && !draftFailed && requestError === null;
  const firstDraft: FirstDraft<'rqs'> | null = firstDraftOf({
    read: view !== null,
    drafted: dg !== null,
    waitsOn: needsRqs ? 'rqs' : null,
    failed: retryOffered,
  });

  /**
   * What holds the approval of the guide on screen, first what the reader can act on, or null when
   * nothing does: Continue approves on exactly that, so a gap, or a check that has not answered,
   * holds it. With no guide drafted yet it names what the first draft waits on, as the step's own
   * lines do.
   */
  const approvalBlock = (): string | null => {
    if (firstDraft !== null) {
      return firstDraft.status === 'needs-upstream'
        ? RQS_NOT_APPROVED
        : firstDraft.status === 'failed'
          ? GUIDE_DRAFT_FAILED_HINT
          : GUIDE_DRAFTING_HINT;
    }

    if (retryOffered) {
      return GUIDE_RETRY_HINT;
    }

    if (!editable || dg.status !== 'unapproved') {
      return GUIDE_UPDATING_HINT;
    }

    if (!authorizes) {
      return RQS_NOT_APPROVED;
    }

    if (openEdits > 0) {
      return EDITS_OPEN_HINT;
    }

    if (asking) {
      return GUIDE_REDRAFTING_HINT;
    }

    if (approving || saving !== null || settling) {
      return SAVING_HINT;
    }

    if (gaps.length > 0) {
      return GUIDE_GAP_HINT;
    }

    if (checking || verdict === null) {
      return !checking &&
        !coverageWaiting &&
        (coverageFailed || coverageLate || coverageError !== null)
        ? GUIDE_CHECK_FAILED_HINT
        : GUIDE_CHECKING_HINT;
    }

    return null;
  };
  const blocked = approvalBlock();
  const approvable = blocked === null;

  const { refusal, continueRun } = useRefusalFocus();
  /**
   * Continue approves a guide not yet approved — anyway over uncertain coverage, which its label
   * then says — and stays on the step over a refusal, which it takes the reader to. Before one is
   * drafted it is held with what the draft waits on. An approved or fixed guide leaves Continue a
   * plain move, and so does the first read still out.
   */
  useContinueAction(
    view === null || frozen || dg?.status === 'approved'
      ? null
      : {
          run: continueRun(approve),
          disabled: !approvable,
          blockedHint: blocked,
          ...(anyway ? { label: GUIDE_APPROVE_ANYWAY_LABEL } : {}),
          busyLabel: APPROVING_LABEL,
        },
  );

  if (view === null) {
    return loadError === null ? (
      <p data-test-id="guide-loading">Reading your guide…</p>
    ) : (
      <p className="chat-error" data-test-id="guide-error" role="alert">
        {loadError}{' '}
        <button type="button" data-test-id="guide-reload" onClick={() => void load()}>
          Try again
        </button>
      </p>
    );
  }

  /**
   * A gap or uncertain line in the check of the guide on screen: the one other state a redraft is
   * offered over. A check is shown only on a guide the study may still change, so a fixed study
   * never gets here.
   */
  const coverageRedraftOffered =
    coverageUnanswered && currentInputs !== null && !asking && !draftWaiting && !retryOffered;
  /**
   * What can be done about the check's lines, shown once: in the gap notice when a gap shows, in
   * the uncertain notice otherwise. "Help me fix this" sends every line, gaps and uncertain alike.
   */
  const fixMessage = coverageFixMessage([...gaps, ...uncertain], rqText);
  const fixCheckId = coverageForNow?.id ?? null;
  /**
   * "Help me fix this" is held while the step is busy, while a redraft is out or late (Sigora's edit
   * would race it on the same guide), while a question's wording is open (the edit would move the
   * version under it), and once it has been sent over the check on screen.
   */
  const fixHeld =
    busy ||
    draftWaiting ||
    draftLate ||
    openEdits > 0 ||
    (fixCheckId !== null && fixSentFor === fixCheckId);
  const coverageActions = (
    <CoverageActions
      onFix={
        fixMessage === null
          ? null
          : () => {
              setFixSentFor(fixCheckId);
              openAskSigora({ message: fixMessage, autoSend: true });
            }
      }
      fixDisabled={fixHeld}
      onRedraft={coverageRedraftOffered ? () => askDraft(true) : null}
      disabled={busy}
      redraftChangedNothing={redraftChangedNothing}
    />
  );
  const reasonShown =
    dg === null
      ? ''
      : (view.history.find((entry) => entry.version === dg.version)?.reasonText ?? '');
  /**
   * What the reconciliation behind the guide on screen changed, shown until the guide is approved:
   * the history keeps the version's line after that.
   */
  const reconciled =
    view.reconciliation !== null &&
    dg !== null &&
    view.reconciliation.version === dg.version &&
    dg.status === 'unapproved'
      ? view.reconciliation
      : null;
  /** The marked version is a redraft over a coverage gap rather than an answer to a change above. */
  const repaired = reconciled?.repair === true;
  // A removed item's id is no question on screen, so only what changed or was added is ever found.
  const marks = new Map<string, ReconciliationItem>(
    (reconciled?.items ?? []).map((item) => [item.id, item]),
  );
  const removedItems = reconciled?.items.filter((item) => item.change === 'removed') ?? [];
  /**
   * The reason an item the reconciliation gave none for is shown with: the version's history line,
   * else the line the reconciliation itself makes, so a mark is never left unexplained.
   */
  const reconciledReason =
    reasonShown !== '' || reconciled === null
      ? reasonShown
      : reconciledVersionText({
          upstream_kinds: reconciled.upstreamKinds,
          unchanged: reconciled.unchanged,
        });

  const coverageRetryOffered =
    checkable && (coverageFailed || coverageLate || coverageError !== null);

  const sections = dg?.content.sections ?? [];
  /** One numbering across the whole guide: the one the coverage check and Sigora use. */
  const questionNumbers = dg === null ? new Map<string, number>() : dgQuestionNumbers(dg.content);
  const imagesById = new Map(view.images.map((image) => [image.file_id, image]));
  /** An image marked to show that no question shows, which the step's conversation can place. */
  const unshownImage =
    editable && view.images.some((image) => image.candidate && image.shown_on === null);
  const allExpanded = sections.length > 0 && sections.every((section) => expanded.has(section.id));
  const rqCount = rqs?.items.length ?? 0;
  const servesLine = (question: DgQuestion): string => {
    if (rqCount > 1 && question.serves_rq_ids.length === rqCount) {
      return 'Serves: every research question';
    }

    const named = question.serves_rq_ids.flatMap((id) => {
      const text = rqText.get(id);

      return text === undefined ? [] : [text];
    });

    return named.length === 0
      ? 'Serves: no current research question'
      : `Serves: ${named.join('; ')}`;
  };

  const renderEdit = (question: DgQuestion, edit: GuideEdit) => {
    const error = editErrors.get(question.id) ?? null;
    const fieldId = `guide-wording-${question.id}`;
    const saveOnEnter = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        save(question);
      }
    };

    return (
      <div
        className="guide-edit"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && saving !== question.id) {
            event.preventDefault();
            closeEdit(question.id);
          }
        }}
      >
        <label className="visually-hidden" htmlFor={fieldId}>
          Question wording
        </label>
        <textarea
          id={fieldId}
          data-test-id="guide-wording"
          value={edit.text}
          maxLength={MAX_DG_QUESTION_CHARS}
          rows={3}
          disabled={saving === question.id}
          ref={(field) => {
            if (field === null) {
              editFields.current.delete(question.id);
            } else {
              editFields.current.set(question.id, field);
            }
          }}
          onChange={(event) => {
            const text = event.target.value;
            typeInto(question.id, (open) => ({ ...open, text }));
          }}
          onKeyDown={saveOnEnter}
        />
        {edit.options.length > 0 && (
          <ol className="guide-edit-options">
            {edit.options.map((option, index) => {
              const optionId = `guide-option-${question.id}-${option.id}`;

              return (
                <li key={option.id}>
                  <label className="visually-hidden" htmlFor={optionId}>
                    Answer {index + 1}
                  </label>
                  <input
                    id={optionId}
                    type="text"
                    data-test-id="guide-option-wording"
                    data-option-id={option.id}
                    value={option.text}
                    maxLength={MAX_DG_OPTION_CHARS}
                    disabled={saving === question.id}
                    onChange={(event) => {
                      const text = event.target.value;
                      typeInto(question.id, (open) => ({
                        ...open,
                        options: open.options.map((typed) =>
                          typed.id === option.id ? { ...typed, text } : typed,
                        ),
                      }));
                    }}
                    onKeyDown={saveOnEnter}
                  />
                </li>
              );
            })}
          </ol>
        )}
        <div className="guide-edit-actions">
          <button
            type="button"
            data-test-id="guide-save"
            disabled={busy || confirming?.questionId === question.id || !savable(edit)}
            onClick={() => save(question)}
          >
            {saving === question.id ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-test-id="guide-cancel"
            disabled={saving === question.id}
            onClick={() => closeEdit(question.id)}
          >
            Cancel
          </button>
        </div>
        {confirming?.questionId === question.id && (
          <ChangeConfirm
            consequences={confirming.consequences}
            busy={busy}
            onConfirm={() => save(question, acknowledgementOf(confirming.consequences))}
            onCancel={() => {
              setConfirming(null);
              setFocusField(question.id);
            }}
          />
        )}
        {error !== null && (
          <p className="chat-error" data-test-id="guide-edit-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="guide" data-test-id="guide">
      {dg !== null && (
        <h2 className="guide-heading">
          This is what people will see,{' '}
          <span data-test-id="guide-minutes">{guideMinutesLabel(dg.content)}</span>.
        </h2>
      )}

      {notice !== null && (
        <p
          ref={refusal}
          className="guide-notice"
          data-test-id="guide-notice"
          role="status"
          tabIndex={-1}
        >
          {notice}
        </p>
      )}

      {/*
        A read that failed over a step already on screen: what is shown is the last read that
        answered, so the step says it may be out of date rather than passing it off as current.
      */}
      {loadError !== null && (
        <p className="chat-error" data-test-id="guide-refresh-error" role="alert">
          Your guide could not be read again, so this may be out of date. {loadError}{' '}
          <button type="button" data-test-id="guide-refresh-retry" onClick={() => void load()}>
            Try again
          </button>
        </p>
      )}

      {frozen && dg === null && (
        <p className="guide-status" data-test-id="guide-none">
          This study has no guide.
        </p>
      )}

      {needsRqs && (
        <p className="guide-status" data-test-id="guide-draft-needs-rqs">
          {RQS_NOT_APPROVED} Sigora drafts the guide from them.
        </p>
      )}

      {reconciled !== null && !reconciled.unchanged && (
        <div className="guide-notice" data-test-id="guide-reconciled">
          <p>
            {repaired
              ? 'Sigora redrafted this guide for the research questions the check found unanswered. What changed is marked below.'
              : reconciledText('guide', reconciled.upstreamKinds)}
          </p>
          {removedItems.length > 0 && (
            <>
              <p>{RECONCILED_REMOVED_HEADING}</p>
              <ul>
                {removedItems.map((item) => (
                  <li key={item.id} data-test-id="guide-reconciled-removed">
                    {item.label}
                    <span className="guide-coverage-reason">
                      {' '}
                      — {item.reason ?? reconciledReason}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {reconciled?.unchanged === true && (
        <p className="guide-notice" data-test-id="guide-reconciled-unchanged">
          {reconciledUnchangedText(reconciled.upstreamKinds)}
        </p>
      )}

      <div aria-live="polite">
        {reconciling && !frozen && (asking || draftWaiting) && (
          <Pending live={false} className="guide-status" testId="guide-reconcile-pending">
            {reconcilePendingText('guide', staleKinds)}
          </Pending>
        )}

        {reconciling && draftLate && (
          <p className="guide-status" data-test-id="guide-reconcile-overdue">
            {reconcileOverdueText('guide')}
          </p>
        )}

        {reconciling && draftFailed && (
          <p className="guide-status" data-test-id="guide-reconcile-failed">
            {reconcileFailedText('guide')}
          </p>
        )}

        {!reconciling && (draftWaiting || firstDraftUnderWay) && (
          <Pending live={false} className="guide-status" testId="guide-draft-pending">
            {dg === null
              ? 'Sigora is drafting your guide from your research questions…'
              : 'Sigora is redrafting your guide for the research questions it doesn’t answer…'}
          </Pending>
        )}

        {!reconciling && draftLate && (
          <p className="guide-status" data-test-id="guide-draft-overdue">
            {dg === null
              ? 'Sigora is taking longer than it should to draft your guide.'
              : 'Sigora is taking longer than it should to redraft your guide.'}
          </p>
        )}

        {!reconciling && draftForNow?.status === 'unavailable' && (
          <p className="guide-status" data-test-id="guide-draft-unavailable">
            {dg === null
              ? 'Sigora could not draft the guide just now.'
              : 'Sigora could not redraft the guide just now.'}
          </p>
        )}

        {!reconciling && draftForNow?.status === 'unreadable' && (
          <p className="guide-status" data-test-id="guide-draft-unreadable">
            {dg === null
              ? 'Sigora’s draft did not come back as a complete guide.'
              : 'Sigora’s redraft did not come back as a complete guide.'}
          </p>
        )}
      </div>

      {retryOffered && (
        <button
          type="button"
          data-test-id="guide-retry"
          disabled={busy}
          onClick={() => askDraft(true)}
        >
          Try again
        </button>
      )}

      {requestError !== null && (
        <p className="chat-error" data-test-id="guide-request-error" role="alert">
          {requestError}
        </p>
      )}

      {dg !== null && (
        <div className="card document-card">
          {/* The guide on screen is the one the change came after, never passed off as current. */}
          {reconciling && (
            <p className="guide-status" data-test-id="guide-before-change">
              {BEFORE_YOUR_CHANGE}
            </p>
          )}

          {sections.length > 0 && (
            <button
              type="button"
              className="guide-show-all text-link muted"
              data-test-id="guide-show-all"
              onClick={() =>
                setExpanded(
                  allExpanded ? new Set() : new Set(sections.map((section) => section.id)),
                )
              }
            >
              {allExpanded ? 'Hide all questions' : 'Show all questions'}
            </button>
          )}

          <ol className="guide-sections" data-test-id="guide-sections">
            {sections.map((section) => {
              // A section with an edit open stays open, so what holds the approval stays in view.
              const open =
                expanded.has(section.id) ||
                (editable && section.questions.some((question) => edits.has(question.id)));
              const bodyId = `guide-section-${section.id}`;
              // The section itself retitled or added by a reconciliation, beside any question in it.
              const sectionMark = marks.get(section.id);
              const sectionChange =
                sectionMark !== undefined && sectionMark.change !== 'removed'
                  ? sectionMark.change
                  : section.questions.some((question) => marks.has(question.id))
                    ? 'changed'
                    : null;

              return (
                <li
                  className="guide-section"
                  data-test-id="guide-section"
                  data-section-id={section.id}
                  key={section.id}
                >
                  <button
                    type="button"
                    className="guide-section-toggle"
                    data-test-id="guide-section-toggle"
                    ref={(element) => {
                      if (element === null) {
                        sectionToggles.current.delete(section.id);
                      } else {
                        sectionToggles.current.set(section.id, element);
                      }
                    }}
                    aria-expanded={open}
                    aria-controls={bodyId}
                    onClick={() =>
                      setExpanded((current) => {
                        const next = new Set(current);
                        if (next.has(section.id)) {
                          next.delete(section.id);
                        } else {
                          next.add(section.id);
                        }

                        return next;
                      })
                    }
                  >
                    <span className="guide-section-title">{section.title}</span>{' '}
                    <span className="guide-section-counts" data-test-id="guide-section-counts">
                      {sectionCounts(section.questions)}
                    </span>
                    {sectionChange !== null && (
                      <span className="guide-tag" data-test-id="guide-section-changed">
                        {CHANGE_MARK_LABELS[sectionChange]}
                      </span>
                    )}
                  </button>

                  {/* A section marked only for a question in it is explained by why the version was made. */}
                  {sectionChange !== null && (
                    <p className="guide-coverage-reason" data-test-id="guide-section-change-reason">
                      {sectionMark?.reason ?? reconciledReason}
                    </p>
                  )}

                  {open && (
                    <ol className="guide-questions" id={bodyId}>
                      {section.questions.map((question) => {
                        const edit = editable ? edits.get(question.id) : undefined;
                        const mark = marks.get(question.id);
                        const image =
                          question.stimulus === undefined
                            ? undefined
                            : imagesById.get(question.stimulus.file_id);

                        return (
                          <li
                            className="guide-question"
                            data-test-id="guide-question"
                            data-question-id={question.id}
                            key={question.id}
                          >
                            {/* Participants see the image first, then the question. */}
                            {question.stimulus !== undefined && (
                              <figure
                                className="guide-question-image"
                                data-test-id="guide-question-image"
                                data-file-id={question.stimulus.file_id}
                              >
                                {/*
                                  Its own dimensions reserve its space before the bytes arrive. An
                                  image the step was told nothing about is still drawn and named,
                                  since participants see it all the same.
                                */}
                                <img
                                  src={materialsFileUrl(studyId, question.stimulus.file_id)}
                                  alt={image?.name ?? IMAGE_CAPTION}
                                  width={image?.width ?? undefined}
                                  height={image?.height ?? undefined}
                                />
                                <figcaption>{IMAGE_CAPTION}</figcaption>
                              </figure>
                            )}

                            <p
                              className="questions-number"
                              data-test-id="guide-question-number"
                              aria-hidden="true"
                            >
                              Question {questionNumbers.get(question.id)}
                            </p>

                            <p className="guide-question-kind" data-test-id="guide-question-kind">
                              {KIND_LABELS[question.type]}
                            </p>

                            {edit === undefined ? (
                              <>
                                <p
                                  className="guide-question-text"
                                  data-test-id="guide-question-text"
                                >
                                  {question.text}
                                </p>
                                {question.options !== undefined && (
                                  <ul
                                    className="guide-question-options"
                                    data-test-id="guide-question-options"
                                  >
                                    {question.options.map((option) => (
                                      <li key={option.id}>{option.text}</li>
                                    ))}
                                  </ul>
                                )}
                              </>
                            ) : (
                              renderEdit(question, edit)
                            )}

                            <p
                              className="guide-question-serves"
                              data-test-id="guide-question-serves"
                            >
                              {servesLine(question)}
                            </p>

                            {question.worded_by === 'user' && (
                              <span className="guide-tag" data-test-id="guide-your-wording">
                                Your wording
                              </span>
                            )}

                            {mark !== undefined && mark.change !== 'removed' && (
                              <>
                                <span className="guide-tag" data-test-id="guide-change-mark">
                                  {CHANGE_MARK_LABELS[mark.change]}
                                </span>
                                <p
                                  className="guide-coverage-reason"
                                  data-test-id="guide-change-reason"
                                >
                                  {mark.reason ?? reconciledReason}
                                </p>
                              </>
                            )}

                            {editable && edit === undefined && (
                              <button
                                type="button"
                                className="guide-edit-open text-link muted"
                                data-test-id="guide-edit"
                                aria-label={`Edit question ${String(questionNumbers.get(question.id))}`}
                                ref={(element) => {
                                  if (element === null) {
                                    editButtons.current.delete(question.id);
                                  } else {
                                    editButtons.current.set(question.id, element);
                                  }
                                }}
                                onClick={() => openEdit(question)}
                              >
                                Edit
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </li>
              );
            })}
          </ol>

          {unshownImage && (
            <p className="guide-notice" data-test-id="guide-images-hint">
              {UNSHOWN_IMAGES_HINT}
            </p>
          )}

          <p className="guide-status" data-test-id="guide-status">
            {STATUS_LABELS[dg.status]}
          </p>

          {checkable && (
            <div className="guide-coverage" aria-live="polite">
              {(checking || coverageWaiting) && (
                <p className="guide-status" data-test-id="guide-coverage-pending">
                  Checking the guide against your research questions…
                </p>
              )}

              {coverageLate && (
                <p className="guide-status" data-test-id="guide-coverage-overdue">
                  Sigora is taking longer than it should to check the guide.
                </p>
              )}

              {coverageFailed && !checking && (
                <p className="guide-status" data-test-id="guide-coverage-failed">
                  Sigora could not check the guide just now.
                </p>
              )}

              {gaps.length > 0 && (
                <CoverageGaps gaps={gaps} rqText={rqText} actions={coverageActions} />
              )}

              {uncertain.length > 0 && (
                <CoverageUncertain
                  uncertain={uncertain}
                  rqText={rqText}
                  actions={gaps.length === 0 ? coverageActions : null}
                />
              )}

              {verdict !== null && gaps.length === 0 && uncertain.length === 0 && (
                <p className="guide-status" data-test-id="guide-coverage-covered">
                  Every research question has a question here that answers it.
                </p>
              )}

              {coverageRetryOffered && (
                <button
                  type="button"
                  data-test-id="guide-coverage-retry"
                  disabled={busy}
                  onClick={() => askCoverage(true)}
                >
                  Try again
                </button>
              )}

              {coverageError !== null && (
                <p className="chat-error" data-test-id="guide-coverage-error" role="alert">
                  {coverageError}
                </p>
              )}
            </div>
          )}

          {approveError !== null && (
            <p
              ref={refusal}
              className="chat-error"
              data-test-id="guide-approve-error"
              role="alert"
              tabIndex={-1}
            >
              {approveError}
            </p>
          )}

          <VersionHistory
            testIdPrefix="guide"
            current={{ version: dg.version, reasonText: reasonShown }}
            entries={historyEntriesOf(view.history)}
          />
        </div>
      )}
    </div>
  );
};
